import React, { useMemo, useState } from 'react';
import { Bookmark, ChevronDown, ExternalLink, Search, X } from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import { getRiskLevel } from '../utils/risk';
import { relativeTime, isWatchlisted, recordKey, formatShortDate } from '../utils/format';
import { getWatchlistItemUpdates } from '../utils/watchlistSnapshots';
import { displayHeadline } from '../utils/displayHeadline';
import { formatSourceLabel } from '../constants/trustedSources';

const SEVERITY_PILL = {
  high: { label: 'High', cls: 'text-[#bd2f2b] bg-[#fae8e7]', edge: 'border-l-[#bd2f2b]' },
  medium: { label: 'Medium', cls: 'text-[#86690c] bg-[#f7f0d2]', edge: 'border-l-transparent' },
  low: { label: 'Low', cls: 'text-[#117049] bg-[#e1f3ea]', edge: 'border-l-transparent' },
  unknown: { label: 'Unrated', cls: 'text-[#4b6679] bg-[#e6edf2]', edge: 'border-l-transparent' },
};
const SEVERITY_RANK = { high: 3, medium: 2, low: 1, unknown: 0 };

const SORTS = [
  ['updated', 'Updated first'],
  ['newest', 'Newest first'],
  ['severity', 'Severity first'],
];

const BTN = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] transition';
const SELECT = 'h-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md px-2.5 pr-7 focus:outline-none focus:border-[#1c4ed8] cursor-pointer hover:bg-[#f6f7fa] transition';

function Pill({ severity }) {
  const style = SEVERITY_PILL[getRiskLevel(severity)] || SEVERITY_PILL.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs leading-none font-semibold px-2 py-1.5 rounded whitespace-nowrap ${style.cls}`}>
      <i className="w-1.5 h-1.5 rounded-full bg-current" />
      {style.label}
    </span>
  );
}

function time(record) {
  const t = record?.published_at ? new Date(record.published_at).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

function WatchlistView({ records, watchlist, toggleWatchlist, setView, onOpenRecord, updatedWatchlistIds }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated');
  const [expanded, setExpanded] = useState(() => new Set());

  const savedRecords = useMemo(() => records.filter(r => isWatchlisted(r, watchlist)), [records, watchlist]);
  const updatedCount = savedRecords.filter(r => updatedWatchlistIds?.has(recordKey(r))).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = savedRecords.filter(r => !q
      || (r.company || '').toLowerCase().includes(q)
      || displayHeadline(r).toLowerCase().includes(q));
    const isUpdated = r => (updatedWatchlistIds?.has(recordKey(r)) ? 1 : 0);
    const by = {
      updated: (a, b) => isUpdated(b) - isUpdated(a) || time(b) - time(a),
      newest: (a, b) => time(b) - time(a),
      severity: (a, b) => SEVERITY_RANK[getRiskLevel(b.severity)] - SEVERITY_RANK[getRiskLevel(a.severity)] || time(b) - time(a),
    }[sort];
    return [...list].sort(by);
  }, [savedRecords, search, sort, updatedWatchlistIds]);

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (savedRecords.length === 0) {
    return (
      <div className="bg-white border border-[#e1e5ec] rounded-lg text-center py-14 px-6">
        <Bookmark size={26} className="mx-auto text-[#76829a]" />
        <p className="mt-2 text-sm font-medium text-[#0d1524]">Nothing saved yet</p>
        <p className="mt-1 text-[13px] text-[#76829a] max-w-sm mx-auto">
          Save a story from All stories or its detail page to follow it here. Updates are flagged as they arrive.
        </p>
        <button type="button" className={`${BTN} mt-3`} onClick={() => setView?.('intelligence')}>Browse stories</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2.5 mb-3.5">
        <p className="text-sm text-[#45516a] mr-auto">
          {savedRecords.length} saved {savedRecords.length === 1 ? 'story' : 'stories'}
          {updatedCount > 0 && <span className="text-[#b05a0a] font-medium"> · {updatedCount} with new updates</span>}
        </p>
        <div className="relative min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#76829a] pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search saved"
            className="h-8 w-full text-[13px] text-[#0d1524] bg-white border border-[#cbd3df] rounded-md pl-9 pr-8 focus:outline-none focus:border-[#1c4ed8] placeholder:text-[#76829a]"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#76829a] hover:text-[#0d1524]">
              <X size={14} />
            </button>
          )}
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className={SELECT} aria-label="Sort saved stories">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="bg-white border border-[#e1e5ec] rounded-lg overflow-hidden">
        {visible.length === 0 ? (
          <div className="text-center py-12 px-6">
            <p className="text-sm font-medium text-[#0d1524]">No saved stories match “{search}”</p>
            <button type="button" className={`${BTN} mt-3`} onClick={() => setSearch('')}>Clear search</button>
          </div>
        ) : visible.map((record, index) => {
          const id = recordKey(record);
          const level = SEVERITY_PILL[getRiskLevel(record.severity)] || SEVERITY_PILL.unknown;
          const isUpdated = updatedWatchlistIds?.has(id);
          const updates = isUpdated ? getWatchlistItemUpdates(record, records) : [];
          const open = expanded.has(id);

          return (
            <div key={id} className={index > 0 ? 'border-t border-[#e1e5ec]' : ''}>
              <div
                className={`grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_72px_100px_72px] gap-3 items-center px-3.5 py-3 border-l-[3px] hover:bg-[#f6f7fa] ${level.edge} ${
                  isUpdated ? 'bg-[#fbeddb]/40' : ''
                }`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenRecord?.(record)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenRecord?.(record); } }}
                  className="min-w-0 cursor-pointer"
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium text-[#1c4ed8]">
                    <CompanyLogo name={record.company} size="sm" />
                    <span className="truncate">{record.company}</span>
                    {isUpdated && (
                      <span className="ml-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-[#e7edfc] text-[#1c4ed8]">
                        {updates.length > 0 ? `${updates.length} new update${updates.length === 1 ? '' : 's'}` : 'Updated'}
                      </span>
                    )}
                  </span>
                  <b className="block mt-1 mb-0.5 text-sm font-semibold leading-snug text-[#0d1524] line-clamp-2">
                    {displayHeadline(record)}
                  </b>
                  <small className="text-[11.5px] text-[#76829a]">
                    {formatSourceLabel(record.source_domain) || 'Source'}
                    {record.published_at ? ` · ${formatShortDate(record.published_at)}` : ''}
                  </small>
                </div>

                <span className="hidden md:block"><Pill severity={record.severity} /></span>
                <span className="hidden md:block text-[13px] text-[#45516a]">
                  {record.published_at ? relativeTime(record.published_at) : '—'}
                </span>

                <div className="flex items-center justify-end gap-0.5">
                  {record.url && (
                    <a href={record.url} target="_blank" rel="noopener noreferrer" title="Open original article"
                      aria-label="Open original article"
                      className="w-8 h-8 rounded-md text-[#76829a] hover:text-[#1c4ed8] hover:bg-[#e7edfc] grid place-items-center transition">
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button type="button" onClick={() => toggleWatchlist(id)} title="Remove from saved" aria-label="Remove from saved"
                    className="w-8 h-8 rounded-md text-[#1c4ed8] hover:bg-[#e7edfc] grid place-items-center transition">
                    <Bookmark size={14} fill="currentColor" />
                  </button>
                </div>
              </div>

              {updates.length > 0 && (
                <div className="border-l-[3px] border-l-[#b05a0a] bg-[#fbeddb]/40 px-3.5 pb-3">
                  <button type="button" onClick={() => toggleExpanded(id)} aria-expanded={open}
                    className="inline-flex items-center gap-1 text-[12.5px] text-[#1c4ed8] hover:underline">
                    {open ? 'Hide changes' : 'Review changes'}
                    <ChevronDown size={13} className={`transition ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="mt-2 rounded-md border border-[#e1e5ec] bg-white overflow-hidden">
                      {updates.slice(0, 3).map((update, i) => (
                        <button
                          key={recordKey(update)}
                          type="button"
                          onClick={() => onOpenRecord?.(update)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-[#f6f7fa] transition ${i > 0 ? 'border-t border-[#e1e5ec]' : ''}`}
                        >
                          <span className="flex items-center justify-between gap-3 text-xs text-[#76829a]">
                            <span className="truncate">{formatSourceLabel(update.source_domain) || 'Source'}</span>
                            {update.published_at && <span className="flex-shrink-0">{relativeTime(update.published_at)}</span>}
                          </span>
                          <span className="block mt-0.5 text-[13px] font-medium leading-snug line-clamp-2 text-[#0d1524]">
                            {displayHeadline(update)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WatchlistView;
